// Section 24.7: numbers on entities are rounded to 1e-6 so long runs do not drift across
// platforms. Applied once when a season completes (and at generation) with structural
// sharing: untouched objects keep their identity (ADR-0010).
import { round6 } from './math';
import type { GameState } from './types';

function roundValue(value: unknown): unknown {
  if (typeof value === 'number') {
    // Integers of this size are already on the 1e-6 grid (the common case: turns, counts).
    if (Number.isInteger(value) && value < INTEGER_LIMIT && value > -INTEGER_LIMIT) return value;
    const r = round6(value);
    return Object.is(r, value) ? value : r;
  }
  if (Array.isArray(value)) {
    let out: unknown[] | null = null;
    const source = value as unknown[];
    for (let i = 0; i < source.length; i++) {
      const r = roundValue(source[i]);
      if (r !== source[i]) {
        out ??= source.slice();
        out[i] = r;
      }
    }
    return out ?? value;
  }
  if (value !== null && typeof value === 'object') {
    let copy: Record<string, unknown> | null = null;
    const source = value as Record<string, unknown>;
    for (const key in source) {
      const v = source[key];
      const r = roundValue(v);
      if (r !== v) {
        copy ??= { ...source };
        copy[key] = r;
      }
    }
    return copy ?? value;
  }
  return value;
}

/** Below this, integer x 1e6 is exact, so round6 returns the integer itself. */
const INTEGER_LIMIT = 1e9;

/** Keys holding live entity numbers. History and series are rounded when written. */
const ROUNDED_KEYS = [
  'people',
  'households',
  'buildings',
  'buildQueue',
  'stocks',
  'toolDurability',
  'seedReserved',
  'plantedPlots',
  'resourceHistory',
  'arrivalGroups',
] as const satisfies readonly (keyof GameState)[];

export function roundState(state: GameState): GameState {
  let next: GameState | null = null;
  for (const key of ROUNDED_KEYS) {
    const before = state[key];
    const after = roundValue(before);
    if (after !== before) {
      next ??= { ...state };
      (next as unknown as Record<string, unknown>)[key] = after;
    }
  }
  return next ?? state;
}
